import { createClient, SupabaseClient } from '@supabase/supabase-js'
import { Database } from './database.types'

// ─── Impersonation token cache ────────────────────────────────────────────────
// Caches the most recently minted target-scoped JWT so the Supabase client
// does not call /api/impersonation/refresh-token on every request.
//
// Cache shape: { token, expiresAt (ms) } | null
// Invalidated when impersonation stops or the session id changes.

interface ImpersonationTokenCache {
  token: string
  /** Unix milliseconds. Refreshed when now > expiresAt - REFRESH_MARGIN_MS. */
  expiresAt: number
  /** The impersonation token this cache entry was minted for. */
  forImpersonationToken: string
}

let _tokenCache: ImpersonationTokenCache | null = null

// Refresh 60 seconds before expiry so the token is always fresh for Supabase.
const REFRESH_MARGIN_MS = 60_000

// Prevent concurrent refresh calls from racing.
let _refreshPromise: Promise<string | null> | null = null

/** Return the URL string from any fetch input type. */
function getUrlString(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return input.url
}

/**
 * True when the URL targets the Supabase REST/RPC/Storage API (not auth endpoints).
 *
 * We intercept /rest/v1, /storage/v1, and /functions/v1 calls — these are the
 * paths that carry RLS-evaluated data. We deliberately do NOT intercept
 * /auth/v1 (session refresh, sign-in, etc.) to avoid infinite recursion when
 * the underlying Supabase client refreshes the admin's session.
 */
function isSupabaseDataUrl(url: string, supabaseUrl: string): boolean {
  try {
    const projectHost = new URL(supabaseUrl).hostname
    const parsed = new URL(url)
    if (parsed.hostname !== projectHost) return false
    // Only intercept data endpoints, not the auth endpoint.
    return (
      parsed.pathname.startsWith('/rest/') ||
      parsed.pathname.startsWith('/storage/') ||
      parsed.pathname.startsWith('/functions/')
    )
  } catch {
    return false
  }
}

/**
 * Capture native fetch BEFORE any patches (PostHog, impersonation header).
 * Using this for the /refresh-token call guarantees no infinite recursion.
 */
const _nativeFetch: typeof fetch = globalThis.fetch.bind(globalThis)

/**
 * Fetch a fresh target-scoped JWT from /api/impersonation/refresh-token.
 *
 * Returns "token|expiresAtIso" on success, null on any failure.
 * Uses _nativeFetch to avoid routing through any window.fetch patches.
 */
async function fetchFreshToken(
  impersonationToken: string,
  adminAccessToken: string,
): Promise<string | null> {
  try {
    const res = await _nativeFetch('/api/impersonation/refresh-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminAccessToken}`,
        'X-Impersonate-Token': impersonationToken,
      },
    })

    if (!res.ok) {
      // 401 with impersonation_* code means the session is invalid.
      if (res.status === 401) {
        // Lazy import — auth store is not available at module load time.
        const { useAuthStore } = await import('~/client/common/stores/auth')
        useAuthStore.getState().clearImpersonationLocal()
      }
      return null
    }

    const body: unknown = await res.json()
    if (
      body !== null &&
      typeof body === 'object' &&
      'token' in body &&
      typeof (body as Record<string, unknown>).token === 'string' &&
      'expires_at' in body &&
      typeof (body as Record<string, unknown>).expires_at === 'string'
    ) {
      const { token, expires_at } = body as { token: string; expires_at: string }
      return `${token}|${expires_at}`
    }
    return null
  } catch {
    return null
  }
}

/**
 * Get a valid target-scoped JWT for the current impersonation session.
 *
 * Returns null when:
 *   - Impersonation is not active
 *   - Admin session has expired
 *   - The refresh call fails (caller falls back to admin auth)
 */
async function getImpersonationJwt(): Promise<string | null> {
  // Lazy import to avoid circular dependency at module load time.
  const { useAuthStore } = await import('~/client/common/stores/auth')
  const state = useAuthStore.getState()

  const impersonationToken = state.impersonationToken
  if (!impersonationToken) return null

  const now = Date.now()

  // Cache hit: token is still fresh AND was minted for this impersonation session.
  if (
    _tokenCache !== null &&
    _tokenCache.forImpersonationToken === impersonationToken &&
    now < _tokenCache.expiresAt - REFRESH_MARGIN_MS
  ) {
    return _tokenCache.token
  }

  // Cache miss, stale, or session changed: refresh.
  // Guard against concurrent calls making multiple refresh requests.
  if (_refreshPromise !== null) {
    return _refreshPromise
  }

  // Get the admin's access token from the Supabase session.
  // supabaseClient.auth.getSession() reads from localStorage — no network call
  // unless the session is expired. If it IS expired and triggers a token refresh,
  // that refresh hits /auth/v1/token which is excluded from isSupabaseDataUrl,
  // so the custom fetch passes it through without calling getImpersonationJwt again.
  const { data: sessionData } = await _supabaseClientRef().auth.getSession()
  const adminAccessToken = sessionData?.session?.access_token
  if (!adminAccessToken) {
    // Admin JWT is gone.
    useAuthStore.getState().clearImpersonationLocal()
    return null
  }

  const capturedToken = impersonationToken

  _refreshPromise = fetchFreshToken(capturedToken, adminAccessToken).then((result) => {
    _refreshPromise = null
    if (result === null) return null

    const pipeIdx = result.indexOf('|')
    if (pipeIdx === -1) return null
    const token = result.slice(0, pipeIdx)
    const expiresAtIso = result.slice(pipeIdx + 1)
    const expiresAt = new Date(expiresAtIso).getTime()

    _tokenCache = { token, expiresAt, forImpersonationToken: capturedToken }
    return token
  })

  return _refreshPromise
}

// ─── Supabase client factory ──────────────────────────────────────────────────

let _client: SupabaseClient<Database> | null = null

/**
 * Internal accessor used inside the custom fetch to get the underlying client
 * without going through the Proxy (which would call getClient → buildCustomFetch
 * in an initialization loop).
 */
function _supabaseClientRef(): SupabaseClient<Database> {
  // _client is guaranteed to be non-null when _supabaseClientRef() is called
  // from within the custom fetch, because getClient() initializes _client
  // before attaching the custom fetch.
  if (!_client) {
    throw new Error('[supabaseClient] _supabaseClientRef called before client initialization')
  }
  return _client
}

function buildCustomFetch(supabaseUrl: string): typeof fetch {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = getUrlString(input)

    // Only intercept Supabase data API calls (not auth endpoints).
    if (!isSupabaseDataUrl(url, supabaseUrl)) {
      return _nativeFetch(input, init)
    }

    // Not in a browser context (SSR/build) — pass through.
    if (typeof window === 'undefined') {
      return _nativeFetch(input, init)
    }

    // Try to get a target-scoped JWT if impersonation is active.
    const targetJwt = await getImpersonationJwt()

    if (!targetJwt) {
      // Not impersonating, or refresh failed — use the original auth header.
      return _nativeFetch(input, init)
    }

    // Swap the Authorization header to the target-scoped JWT.
    const headers = new Headers(init?.headers)
    headers.set('Authorization', `Bearer ${targetJwt}`)
    return _nativeFetch(input, { ...init, headers })
  }
}

function getClient(): SupabaseClient<Database> {
  const url = import.meta.env.VITE_SUPABASE_URL
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY
  if (!url || !key) {
    throw new Error('VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY must be set (e.g. inject env via npx dotenv -e .env.preview when building)')
  }
  if (!_client) {
    _client = createClient<Database>(url, key, {
      global: {
        // Custom fetch: intercepts Supabase REST/Storage/Functions calls during
        // impersonation to swap the Authorization header to a target-scoped JWT.
        // Non-impersonated calls and auth-endpoint calls pass through unchanged.
        // The /api/** fetch wrapper in posthog.ts is non-overlapping (different host).
        fetch: buildCustomFetch(url),
      },
    })
  }
  return _client
}

/** Lazy-initialized so prerender/build does not require env at module load. */
export const supabaseClient = new Proxy({} as SupabaseClient<Database>, {
  get(_, prop) {
    const client = getClient()
    const value = client[prop as keyof SupabaseClient<Database>]
    return typeof value === 'function' ? (value as (...args: unknown[]) => unknown).bind(client) : value
  },
})

/**
 * Invalidate the impersonation JWT cache.
 * Called when impersonation stops or a new session starts so the next Supabase
 * data request fetches a fresh target-scoped JWT (or falls back to admin auth).
 */
export function invalidateImpersonationTokenCache(): void {
  _tokenCache = null
}
